/**
 * Authentication Context Provider
 * Manages authentication state across the application
 * Requirements: 6.1, 6.2, 6.3, 6.5, 6.7, 12.2
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  signIn,
  signUp,
  signOut,
  resetPassword as amplifyResetPassword,
  confirmResetPassword as amplifyConfirmResetPassword,
  confirmSignIn,
  getCurrentUser,
  fetchAuthSession,
} from 'aws-amplify/auth';
import axios from 'axios';
import type { AuthContextType, AuthUser, AuthState, QrLoginParams } from '../types/auth.types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

export const AuthContext = createContext<AuthContextType | null>(null);

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  requiresNewPassword: false,
  pendingUsername: undefined,
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>(initialState);

  const extractUserFromSession = async (): Promise<AuthUser | null> => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;
      if (!idToken) return null;

      const payload = idToken.payload;

      // Check for admin role in custom:role attribute
      const customRole = payload['custom:role'] as string | undefined;
      const isAdminFromToken = customRole === 'ADMIN';

      const basicUser: AuthUser = {
        id: payload.sub as string,
        email: payload.email as string,
        name: payload.name as string | undefined,
        permissionLevel: payload['custom:permissionLevel'] as AuthUser['permissionLevel'],
        isAdmin: isAdminFromToken,
        companyId: '', // デフォルト値（プロファイル取得成功時に上書きされる）
      };

      // Try to fetch profile from backend to get the most up-to-date info
      try {
        const idTokenString = idToken.toString();
        if (idTokenString && API_BASE_URL) {
          const response = await axios.get(`${API_BASE_URL}/profile`, {
            headers: { Authorization: idTokenString },
          });
          if (response.data?.user) {
            console.log('[AuthContext] Profile fetched from backend:', response.data.user);
            return {
              ...basicUser,
              name: response.data.user.name || basicUser.name,
              isAdmin: response.data.user.isAdmin || basicUser.isAdmin,
              permissionLevel: response.data.user.permissionLevel || basicUser.permissionLevel,
              companyId: response.data.user.company?.id || '',
              companyName: response.data.user.company?.name || '',
            };
          }
        }
      } catch (error) {
        // Profile fetch failed, use basic user from token
        console.warn('[AuthContext] Failed to fetch profile from backend, using token data:', error);
        console.log('[AuthContext] Using token-based user:', basicUser);
      }

      return basicUser;
    } catch {
      return null;
    }
  };

  const checkAuthStatus = useCallback(async () => {
    try {
      await getCurrentUser();
      const user = await extractUserFromSession();
      setState({
        user,
        isAuthenticated: !!user,
        isLoading: false,
        error: null,
        requiresNewPassword: false,
        pendingUsername: undefined,
      });
    } catch {
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        requiresNewPassword: false,
        pendingUsername: undefined,
      });
    }
  }, []);

  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  const login = async (username: string, password: string): Promise<void> => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const result = await signIn({ username, password });
      
      // Check if new password is required (first login after admin creation)
      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          requiresNewPassword: true,
          pendingUsername: username,
          error: null,
        }));
        return;
      }
      
      const user = await extractUserFromSession();
      setState({
        user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        requiresNewPassword: false,
        pendingUsername: undefined,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'ログインに失敗しました';
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      throw error;
    }
  };

  const completeNewPassword = async (newPassword: string): Promise<void> => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const result = await confirmSignIn({ challengeResponse: newPassword });
      
      if (result.isSignedIn) {
        const user = await extractUserFromSession();
        setState({
          user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
          requiresNewPassword: false,
          pendingUsername: undefined,
        });
      } else {
        throw new Error('パスワード変更に失敗しました');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'パスワード変更に失敗しました';
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      throw error;
    }
  };

  /**
   * mock idToken からユーザー claims を抽出する（ローカル開発専用 / 決定 D5）。
   * バックエンド (qr-auth.handler buildMockTokens) は本物の Cognito JWT ではなく
   * `mock-id.<username>.<timestamp>` を base64url エンコードした不透明文字列を返す。
   */
  const decodeMockIdToken = (idToken: string): AuthUser => {
    const fallback: AuthUser = {
      id: 'local-mock-user',
      email: '',
      isAdmin: false,
      companyId: '',
    };

    try {
      // 1) 本物の JWT 形式（payload が claims を含む）を優先的に試行
      const parts = idToken.split('.');
      if (parts.length === 3) {
        const json = JSON.parse(
          decodeURIComponent(
            atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
              .split('')
              .map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`)
              .join('')
          )
        ) as Record<string, unknown>;
        const customRole = json['custom:role'] as string | undefined;
        return {
          id: (json.sub as string) || fallback.id,
          email: (json.email as string) || '',
          name: json.name as string | undefined,
          permissionLevel: json['custom:permissionLevel'] as AuthUser['permissionLevel'],
          isAdmin: customRole === 'ADMIN',
          companyId: '',
        };
      }

      // 2) 不透明な mock 形式（`mock-id.<username>.<timestamp>` の base64url）を試行
      const decoded = atob(idToken.replace(/-/g, '+').replace(/_/g, '/'));
      const segments = decoded.split('.');
      if (segments.length >= 2) {
        const username = segments[1];
        return { ...fallback, id: username || fallback.id, email: username || '' };
      }
    } catch {
      // デコード失敗時はフォールバックを返す
    }

    return fallback;
  };

  /**
   * QR ログイン（Custom Auth 駆動 / qr-login-token-issuance）。
   * - 本番（mode: 'cognito'）: signIn（CUSTOM_WITHOUT_SRP）→ confirmSignIn でカスタム
   *   チャレンジに challengeSecret を応答し、成功で extractUserFromSession を流用する。
   * - ローカル（mode: 'mock'）: Cognito を呼ばず mock トークンから直接ユーザー状態を構築する。
   * challengeSecret はメモリ上のみで扱い、使用後は破棄する（永続化しない）。
   */
  const qrLogin = async (params: QrLoginParams): Promise<void> => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      if (params.mode === 'mock') {
        const user = decodeMockIdToken(params.tokens.idToken);
        setState({
          user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
          requiresNewPassword: false,
          pendingUsername: undefined,
        });
        return;
      }

      // 本番: Cognito Custom Auth を実行。
      const { username, challengeSecret } = params;
      const result = await signIn({
        username,
        options: { authFlowType: 'CUSTOM_WITHOUT_SRP' },
      });

      if (result.nextStep?.signInStep !== 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
        throw new Error('カスタム認証チャレンジを開始できませんでした');
      }

      const confirmResult = await confirmSignIn({ challengeResponse: challengeSecret });
      if (!confirmResult.isSignedIn) {
        throw new Error('カスタム認証に失敗しました');
      }

      const user = await extractUserFromSession();
      setState({
        user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        requiresNewPassword: false,
        pendingUsername: undefined,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'QR ログインに失敗しました';
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      throw error;
    }
  };

  const register = async (email: string, password: string): Promise<void> => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
          },
        },
      });
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '登録に失敗しました';
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      await signOut();
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        requiresNewPassword: false,
        pendingUsername: undefined,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'ログアウトに失敗しました';
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      throw error;
    }
  };

  const resetPassword = async (email: string): Promise<void> => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      await amplifyResetPassword({ username: email });
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'パスワードリセットの送信に失敗しました';
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      throw error;
    }
  };

  const confirmResetPassword = async (
    email: string,
    code: string,
    newPassword: string
  ): Promise<void> => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      await amplifyConfirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword,
      });
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'パスワードリセットに失敗しました';
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      throw error;
    }
  };

  const clearError = (): void => {
    setState((prev) => ({ ...prev, error: null }));
  };

  const value: AuthContextType = {
    ...state,
    login,
    register,
    logout,
    resetPassword,
    confirmResetPassword,
    completeNewPassword,
    qrLogin,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
